/**
 * RadarDashboardPage.js
 * 상한가 레이더 — 대시보드 (개발계획서 9.3)
 *
 * C:\Future\limit_up_radar 의 Python 배치가 Firestore radar_events/radar_ticker_stats
 * 에 기록한 데이터를 /api/radar/* 를 통해 조회하여 카드 그리드로 표시한다.
 * "1이벤트=1카드" 원칙(개발계획서 3장·9.5) — 카드 하나만 봐도 종목명·상한가
 * 정보·D+1 결과·상승 사유 요지가 전부 보이도록 구성한다.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Flame, RefreshCw, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import apiService from '../../services/apiService';

/* ── 애니메이션 ─────────────────────────────────────────────────────────────── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const spin   = keyframes`from{transform:rotate(0)}to{transform:rotate(360deg)}`;

/* ── 레이아웃 ────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  min-height:100vh;background:#0a0a0f;color:#e2e8f0;
  font-family:'Inter','Pretendard',sans-serif;padding:20px;
`;
const TopBar = styled.div`
  display:flex;align-items:center;gap:12px;
  margin-bottom:24px;padding-bottom:20px;border-bottom:1px solid #1e2030;flex-wrap:wrap;
`;
const BackBtn = styled.button`
  background:none;border:none;color:#64748b;cursor:pointer;padding:8px;border-radius:8px;
  &:hover{background:#1e2030;color:#e2e8f0}
`;
const TitleArea = styled.div`flex:1;min-width:200px;`;
const PageTitle = styled.h1`
  font-size:22px;font-weight:800;margin:0;
  background:linear-gradient(135deg,#f97316,#ef4444);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;
`;
const PageSub = styled.p`margin:2px 0 0;font-size:12px;color:#64748b;`;

const RefreshBtn = styled.button`
  display:flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;
  font-size:13px;font-weight:600;cursor:pointer;
  background:rgba(249,115,22,0.1);color:#f97316;border:1px solid #f97316;
  &:hover{opacity:.8}&:disabled{opacity:.4;cursor:not-allowed}
`;
const SpinIcon = styled(RefreshCw)`
  ${p => p.$spinning && css`animation:${spin} 1s linear infinite;`}
`;

/* ── 요약 카드 ───────────────────────────────────────────────────────────────── */
const StatGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:12px;
  margin-bottom:24px;animation:${fadeIn} .4s ease;
`;
const StatCard = styled.div`
  background:#111827;border-radius:14px;padding:18px 20px;border:1px solid #1e2030;
`;
const StatLabel = styled.p`margin:0 0 8px;font-size:10px;color:#64748b;font-weight:700;text-transform:uppercase;letter-spacing:.06em;`;
const StatVal   = styled.div`
  font-size:26px;font-weight:800;
  color:${p => p.$pos === true ? '#10b981' : p.$pos === false ? '#ef4444' : '#e2e8f0'};
`;
const StatSub = styled.p`margin:4px 0 0;font-size:11px;color:#475569;`;

/* ── 필터 ────────────────────────────────────────────────────────────────────── */
const FilterBar = styled.div`
  display:flex;gap:10px;flex-wrap:wrap;align-items:center;margin-bottom:20px;
`;
const FilterInput = styled.input`
  background:#111827;border:1px solid #1e2030;border-radius:8px;color:#e2e8f0;
  padding:7px 10px;font-size:12px;
  &:focus{outline:none;border-color:#f97316}
`;
const FilterSelect = styled.select`
  background:#111827;border:1px solid #1e2030;border-radius:8px;color:#e2e8f0;
  padding:7px 10px;font-size:12px;
  &:focus{outline:none;border-color:#f97316}
`;

/* ── 카드 그리드 ─────────────────────────────────────────────────────────────── */
const CardGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:14px;
  animation:${fadeIn} .4s ease;
`;
const EventCard = styled.div`
  background:#111827;border:1px solid #1e2030;border-radius:14px;padding:18px;
  cursor:pointer;transition:all .2s;
  &:hover{border-color:#f97316;transform:translateY(-2px);box-shadow:0 8px 20px rgba(249,115,22,0.15)}
`;
const CardTop = styled.div`display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;`;
const StockName = styled.div`font-size:15px;font-weight:700;color:#e2e8f0;`;
const StockMeta = styled.div`font-size:11px;color:#64748b;margin-top:2px;`;
const StreakBadge = styled.span`
  font-size:10px;font-weight:700;padding:3px 8px;border-radius:99px;white-space:nowrap;
  background:${p => p.$n > 1 ? 'rgba(249,115,22,0.15)' : 'rgba(100,116,139,0.12)'};
  color:${p => p.$n > 1 ? '#f97316' : '#64748b'};
`;
const HitTimeRow = styled.div`
  display:flex;align-items:center;gap:5px;font-size:11px;color:#94a3b8;margin-bottom:10px;
`;
const MaterialBadge = styled.span`
  display:inline-block;font-size:10px;font-weight:700;padding:2px 8px;border-radius:6px;
  background:rgba(139,92,246,0.12);color:#8b5cf6;margin-bottom:8px;
`;
const SummaryText = styled.p`
  margin:0 0 12px;font-size:12px;color:#94a3b8;line-height:1.5;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;
`;
const D1Row = styled.div`
  display:flex;align-items:center;gap:6px;font-size:12px;font-weight:700;
  color:${p => p.$up === true ? '#10b981' : p.$up === false ? '#ef4444' : '#64748b'};
`;
const ProbBadge = styled.div`
  display:flex;align-items:center;justify-content:space-between;gap:6px;
  margin-bottom:10px;padding:6px 10px;border-radius:8px;font-size:11px;font-weight:700;
  background:${p => p.$rate >= 50 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)'};
  color:${p => p.$rate >= 50 ? '#10b981' : '#ef4444'};
`;

const Empty = styled.div`padding:60px;text-align:center;color:#475569;font-size:13px;`;

/* ── 포맷 헬퍼 ───────────────────────────────────────────────────────────────── */
const fmtHitTime = (event) => {
  if (event.hit_time_precision === 'daily_close_only' || !event.hit_time) return '종가 기준';
  const ts = event.hit_time._seconds ? new Date(event.hit_time._seconds * 1000) : new Date(event.hit_time);
  return `약 ${ts.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}경`;
};

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   메인 컴포넌트
═══════════════════════════════════════════════════════════════════════════ */
export default function RadarDashboardPage() {
  const navigate = useNavigate();

  // 필터(날짜·시장·재료유형)를 로컬 state가 아니라 URL 쿼리에 저장한다.
  // 상세페이지로 넘어갔다가 뒤로 돌아왔을 때 이 화면이 다시 마운트되며
  // state가 초기화되던 문제(날짜를 다시 골라야 하는 불편)를 없애기 위함 —
  // URL이 곧 상태이므로 뒤로가기만으로 그대로 복원된다.
  const [searchParams, setSearchParams] = useSearchParams();
  const date         = searchParams.get('date') || todayStr();
  const market       = searchParams.get('market') || '';
  const materialType = searchParams.get('material_type') || '';

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });   // 필터 변경마다 히스토리를 쌓지 않음
  };

  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [eventsData, summaryData] = await Promise.all([
        apiService.getRadarEvents({ date, market, materialType }),
        apiService.getRadarSummary(),
      ]);
      setEvents(eventsData);
      setSummary(summaryData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [date, market, materialType]);

  useEffect(() => { load(); }, [load]);

  const materialTypes = Object.keys(summary?.material_distribution || {});

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>상한가 레이더</PageTitle>
          <PageSub>상한가 종목 탐지 · 상승 사유 · D+1 결과 · 재료 지속성</PageSub>
        </TitleArea>
        <RefreshBtn onClick={load} disabled={loading}>
          <SpinIcon size={14} $spinning={loading} />
          새로고침
        </RefreshBtn>
      </TopBar>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 10,
          padding: '12px 16px', marginBottom: 20, color: '#f87171', fontSize: 13 }}>
          오류: {error} (limit_up_radar 백엔드/Firestore 연결을 확인하세요)
        </div>
      )}

      <StatGrid>
        <StatCard>
          <StatLabel>오늘 상한가</StatLabel>
          <StatVal>{summary ? summary.today_count : '-'}</StatVal>
          <StatSub>종목</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>최근 20일 D+1 상승률</StatLabel>
          <StatVal $pos={summary?.recent_20d_d1_up_rate >= 50 ? true : summary?.recent_20d_d1_up_rate < 50 ? false : undefined}>
            {summary?.recent_20d_d1_up_rate != null ? `${summary.recent_20d_d1_up_rate}%` : '-'}
          </StatVal>
          <StatSub>최근 {summary ? summary.recent_20d_count : '-'}건 기준</StatSub>
        </StatCard>
        <StatCard>
          <StatLabel>주요 재료유형</StatLabel>
          <StatVal style={{ fontSize: 16 }}>
            {materialTypes.length
              ? materialTypes.sort((a, b) => summary.material_distribution[b] - summary.material_distribution[a])[0]
              : '-'}
          </StatVal>
          <StatSub>최근 20일 기준</StatSub>
        </StatCard>
      </StatGrid>

      <FilterBar>
        <FilterInput type="date"
          value={`${date.slice(0,4)}-${date.slice(4,6)}-${date.slice(6,8)}`}
          onChange={e => updateFilter('date', e.target.value.replace(/-/g, ''))} />
        <FilterSelect value={market} onChange={e => updateFilter('market', e.target.value)}>
          <option value="">전체 시장</option>
          <option value="KOSPI">KOSPI</option>
          <option value="KOSDAQ">KOSDAQ</option>
        </FilterSelect>
        <FilterSelect value={materialType} onChange={e => updateFilter('material_type', e.target.value)}>
          <option value="">전체 재료유형</option>
          {['정책·테마형', '실적·공시형', '수급·차익형', '루머·미확인형', '미확인'].map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </FilterSelect>
      </FilterBar>

      {events.length === 0 ? (
        <Empty>{loading ? '불러오는 중...' : '해당 조건의 상한가 종목이 없습니다.'}</Empty>
      ) : (
        <CardGrid>
          {events.map(e => (
            <EventCard key={e.id} onClick={() => navigate(`/labs/radar/${e.id}`)}>
              <CardTop>
                <div>
                  <StockName>{e.name}</StockName>
                  <StockMeta>{e.ticker} · {e.market}</StockMeta>
                </div>
                {e.consecutive_days > 1 && <StreakBadge $n={e.consecutive_days}>연속 {e.consecutive_days}일</StreakBadge>}
              </CardTop>

              <HitTimeRow>
                <Clock size={12} />{fmtHitTime(e)}
                {e.close_price != null && (
                  <span style={{ marginLeft: 'auto', color: '#f97316', fontWeight: 700 }}>
                    {e.close_price.toLocaleString()}원
                    {e.change_rate != null && ` (+${e.change_rate.toFixed(1)}%)`}
                  </span>
                )}
              </HitTimeRow>

              {e.ticker_stats?.d1_up_rate != null && (
                <ProbBadge $rate={e.ticker_stats.d1_up_rate * 100}>
                  <span>D+1 상승확률</span>
                  <span>{(e.ticker_stats.d1_up_rate * 100).toFixed(0)}% ({e.ticker_stats.limit_up_count_10y}건 중)</span>
                </ProbBadge>
              )}

              <MaterialBadge>{e.reason?.material_type || '미확인'}</MaterialBadge>
              <SummaryText>{e.reason?.summary_text || '사유 미확인 (수급성 추정)'}</SummaryText>

              {e.d1_stats ? (
                <D1Row $up={e.d1_stats.up}>
                  {e.d1_stats.up ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  D+1 {(e.d1_stats.close_ratio * 100).toFixed(1)}%
                </D1Row>
              ) : (
                <D1Row>D+1 결과 대기 중</D1Row>
              )}
            </EventCard>
          ))}
        </CardGrid>
      )}
    </Page>
  );
}
